import { useMemo } from 'react';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useGetOfficeById } from '~/hooks/useGetOfficeById';
import { getAllProfessionTypeOptions } from '~/utils/DentistUtils';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

interface IProfessionOption {
  code: string;
  name: string;
}

export const useProfessionTypeOptions = (officeId: string): IProfessionOption[] => {
  const allProfessions = useAppSelector((state) => state.user.allProfessions);
  const office = useGetOfficeById(officeId);

  return useMemo(() => {
    // Always include EFDA in the base list so the remote config
    // (professionsByState) is the single source of truth for which
    // professions are available per state — matching the behavior of
    // SelectProfessionTypePopup.
    const allOptions: IProfessionOption[] = getAllProfessionTypeOptions(allProfessions, {
      showEfda: true,
    });

    const officeState = office?.address?.[0]?.state;

    if (!officeId || !officeState) {
      return allOptions;
    }

    const professionsByState = RemoteConfigUtils.getProfessionsByState();
    const stateProfessions = professionsByState[officeState];

    if (!stateProfessions || stateProfessions.length === 0) {
      return allOptions;
    }

    return allOptions.filter((option) => stateProfessions.includes(option.code));
  }, [allProfessions, office, officeId]);
};
